import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const adminApiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/admin`,
  // Send/receive the httpOnly admin session + CSRF cookies. (#774)
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Read the readable admin CSRF cookie set by the backend at login. The token is
 * echoed in the X-CSRF-Token header so the server can double-submit-verify it.
 */
export const getAdminCsrfToken = (): string | null => {
  const name = 'admin_csrf_token=';
  const parts = document.cookie ? document.cookie.split(';') : [];
  for (const part of parts) {
    const cookie = part.trim();
    if (cookie.startsWith(name)) {
      try {
        return decodeURIComponent(cookie.substring(name.length));
      } catch (error) {
        // A malformed percent-encoding must not crash the request interceptor.
        console.error('Failed to decode admin CSRF token:', error);
        return null;
      }
    }
  }
  return null;
};

// Request interceptor: attach the CSRF token for cookie-based auth. Auth itself
// travels via the httpOnly cookie, so no token is read from localStorage. (#774)
adminApiClient.interceptors.request.use((config) => {
  const csrfToken = getAdminCsrfToken();
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});

// Response interceptor: on an auth failure, send the admin back to login.
// (The session is a cookie the server manages; there is no client-side refresh.)
adminApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const url: string | undefined = error.config?.url;
    const isAuthCall = url?.includes('/auth/login') || url?.includes('/auth/logout');
    if ((status === 401 || status === 403) && !isAuthCall) {
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Admin API interfaces
export interface AdminUser {
  id: string;
  email: string;
  username: string;
  display_name: string;
  role: 'super_admin' | 'admin' | 'support' | 'readonly';
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  last_login?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  admin: AdminUser;
  expires_in: number;
}

export interface DashboardStats {
  total_users: number;
  active_users: number;
  new_users_today: number;
  total_sessions: number;
  active_sessions: number;
  sessions_today: number;
  system_health: string;
}

export interface User {
  id: string;
  email: string;
  display_name: string;
  username?: string;
  location?: string;
  friend_code?: string;
  is_active: boolean;
  is_verified: boolean;
  email_verified: boolean;
  sharing_enabled: boolean;
  created_at: string;
  last_seen: string;
}

export interface Session {
  id: string;
  host_user_id: string;
  client_user_id: string;
  host_display_name?: string;
  client_display_name?: string;
  status: 'pending' | 'active' | 'completed' | 'cancelled' | 'failed' | 'expired';
  session_name?: string;
  client_ip?: string;
  data_used_bytes?: number;
  created_at: string;
  started_at?: string;
  ended_at?: string;

  // Device-aware VPN hosting fields
  host_device_id?: string;
  host_device_name?: string;
}

export interface AuditLog {
  id: string;
  admin_id?: string;
  action: string;
  resource_type?: string;
  resource_id?: string;
  details?: string;
  ip_address: string;
  user_agent?: string;
  timestamp: string;
  success: boolean;
  error_message?: string;
}

export interface SystemHealthData {
  status: string;
  database_healthy: boolean;
  redis_healthy: boolean;
  disk_usage_percent: number;
  memory_usage_percent: number;
  cpu_usage_percent: number;
  uptime_seconds: number;
}

export interface Device {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  platform: string;
  status: 'pending' | 'paired' | 'active' | 'inactive' | 'revoked';
  mesh_ip?: string;
  public_key?: string;
  can_route_traffic: boolean;
  can_accept_routes: boolean;
  last_heartbeat_at?: string;
  last_seen_at?: string;
  paired_at?: string;
  created_at: string;

  // VPN hosting fields (device-aware VPN)
  is_hosting_enabled?: boolean;
  wireguard_public_key?: string;
  wireguard_ip?: string;
  active_sessions_count?: number;
}

export interface DevicePermission {
  id: string;
  device_id: string;
  device_name?: string;
  device_owner_id: string;
  device_owner_name?: string;
  grantee_user_id: string;
  grantee_name?: string;
  status: 'pending' | 'approved' | 'rejected';
  can_route_traffic: boolean;
  can_accept_routes: boolean;
  granted_at?: string;
  created_at: string;
}

export interface ConnectionNode {
  id: string;
  host: string;
  region: string;
  lat?: number | null;
  lng?: number | null;
  status: 'active' | 'draining' | 'unhealthy' | 'offline' | 'maintenance' | string;
  capacity: number;
  current_load: number;
  load_percentage: number;

  // Performance metrics
  avg_latency_ms?: number | null;
  packet_loss_pct?: number | null;
  throughput_mbps?: number | null;
  success_rate_pct?: number | null;
  cpu_usage_pct?: number | null;
  mem_usage_pct?: number | null;
  api_latency_ms?: number | null;

  // Static metadata
  version?: string | null;
  port?: number | null;
  ipv4?: string | null;
  ipv6?: string | null;
  city?: string | null;
  country?: string | null;
  isp?: string | null;

  // Operational
  maintenance_mode: number;
  uptime_seconds?: number | null;

  last_heartbeat_at: string;
  created_at: string;
  updated_at: string;
}

export interface Friend {
  friendship_id: string;
  friend: {
    id: string;
    email: string;
    display_name: string;
    friend_code: string;
  };
  auto_approve_connections: boolean;
  bandwidth_limit_mbps?: number;
  daily_data_limit_gb?: number;
  accepted_at?: string;
}

export interface EmailQueueStats {
  pending_in_queue: number;
  successful: number;
  failed: number;
  pending_results: number;
  total_tracked: number;
  failed_tasks: Array<{
    task_id: string;
    result: string | Record<string, unknown>;
    traceback: string;
    date_done: string;
  }>;
}

export interface PendingEmailTask {
  task_id: string;
  task_name: string;
  args: unknown[];
  kwargs: Record<string, unknown>;
  eta: string | null;
  retries: number;
}

export interface FailedEmailTask {
  task_id: string;
  task_name: string;
  error: string | Record<string, unknown>;
  traceback: string;
  date_done: string;
  args: unknown[];
  kwargs: Record<string, unknown>;
}

export interface EmailLog {
  id: string;
  email_type: string;
  recipient_email: string;
  subject: string;
  status: string;
  task_id: string | null;
  error_message: string | null;
  retry_count: number;
  html_content: string | null;
  text_content: string | null;
  extra_data: Record<string, unknown> | null;
  user_id: string | null;
  created_at: string;
  queued_at: string | null;
  sent_at: string | null;
  failed_at: string | null;
  updated_at: string;
}

export interface EmailLogListResponse {
  total: number;
  logs: EmailLog[];
  page: number;
  page_size: number;
}

export interface EmailLogStats {
  total_emails: number;
  queued: number;
  sent: number;
  failed: number;
  by_type: Record<string, number>;
}

// Email Analytics interfaces (Issue #126)
export interface EmailAnalyticsDashboard {
  total_emails: number;
  sent_count: number;
  failed_count: number;
  queued_count: number;
  success_rate_percent: number;
  avg_delivery_time_seconds: number;
  emails_sent_today: number;
  emails_sent_this_week: number;
  emails_sent_this_month: number;
  top_failure_reasons: Array<{ reason: string; count: number }>;
  emails_by_type: Record<string, number>;
  current_queue_depth: number;
  queue_processing_rate: number;
  estimated_queue_clear_minutes: number;
}

export interface EmailTrendDataPoint {
  date: string;
  sent: number;
  failed: number;
  total: number;
}

export interface EmailTrends {
  period: string;
  data_points: EmailTrendDataPoint[];
}

export interface EmailWorkerStatus {
  workers_active: number;
  workers_total: number;
  redis_healthy: boolean;
  queue_depth: number;
  oldest_pending_task_age_seconds: number | null;
  processing_lag_seconds: number;
}

// User Analytics interfaces (Issue #127)
export interface UserAnalyticsDashboard {
  total_users: number;
  active_users: number;
  inactive_users: number;
  unverified_users: number;
  users_registered_today: number;
  users_registered_this_week: number;
  users_registered_this_month: number;
  growth_rate_percent: number;
  users_active_last_7_days: number;
  users_active_last_30_days: number;
  users_with_devices: number;
  users_with_sessions: number;
  users_with_friends: number;
  auth_providers: Record<string, number>;
  registration_trend: Array<{ date: string; count: number }>;
}

export interface UserActivityDetail {
  user_id: string;
  total_sessions: number;
  total_devices: number;
  total_friends: number;
  last_login: string | null;
  sessions_hosted: number;
  sessions_joined: number;
  devices_by_platform: Record<string, number>;
  recent_sessions: Array<{
    id: string;
    role: string;
    status: string;
    created_at: string;
    ended_at: string | null;
  }>;
}

// Admin API functions
export const adminApi = {
  // Authentication
  login: (data: LoginRequest) =>
    adminApiClient.post<LoginResponse>('/auth/login', data),

  logout: () =>
    adminApiClient.post('/auth/logout'),

  getCurrentAdmin: () =>
    adminApiClient.get<AdminUser>('/me'),

  updateAdminPassword: (data: { current_password: string; new_password: string }) =>
    adminApiClient.put('/me/password', data),

  // Dashboard
  getDashboardStats: () =>
    adminApiClient.get<DashboardStats>('/dashboard/stats'),

  // User Management
  getUsers: (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    active_only?: boolean;
  }) =>
    adminApiClient.get<User[]>('/users', { params }),

  getUser: (userId: string) =>
    adminApiClient.get<User>(`/users/${userId}`),

  updateUserStatus: (userId: string, isActive: boolean) =>
    adminApiClient.put(`/users/${userId}/status?is_active=${isActive}`),

  // Session Management
  getSessions: (params?: {
    skip?: number;
    limit?: number;
    status_filter?: string;
    user_id?: string;
  }) =>
    adminApiClient.get<Session[]>('/sessions', { params }),

  terminateSession: (sessionId: string) =>
    adminApiClient.delete(`/sessions/${sessionId}`),

  deleteSession: (sessionId: string) =>
    adminApiClient.delete(`/sessions/${sessionId}`),

  // Audit Logs
  getAuditLogs: (params?: {
    skip?: number;
    limit?: number;
    action_filter?: string;
    admin_filter?: string;
    days?: number;
  }) =>
    adminApiClient.get<AuditLog[]>('/audit-logs', { params }),

  // Admin Management
  listAdmins: (params?: { skip?: number; limit?: number }) =>
    adminApiClient.get<AdminUser[]>('/admins', { params }),

  createAdmin: (data: {
    email: string;
    username: string;
    display_name: string;
    password: string;
    role: string;
  }) =>
    adminApiClient.post<AdminUser>('/admins', data),

  updateAdmin: (adminId: string, data: Partial<Pick<AdminUser, 'display_name' | 'role' | 'is_active'>>) =>
    adminApiClient.put<AdminUser>(`/admins/${adminId}`, data),

  deleteAdmin: (adminId: string) =>
    adminApiClient.delete(`/admins/${adminId}`),

  // System Health
  getSystemHealth: () =>
    adminApiClient.get<SystemHealthData>('/system/health'),

  // Device Management
  getDevices: (params?: {
    skip?: number;
    limit?: number;
    user_id?: string;
    status?: string;
  }) =>
    adminApiClient.get<Device[]>('/devices', { params }),

  getDevice: (deviceId: string) =>
    adminApiClient.get<Device>(`/devices/${deviceId}`),

  getDevicePermissions: (params?: {
    skip?: number;
    limit?: number;
    device_id?: string;
    grantee_id?: string;
    status?: string;
  }) =>
    adminApiClient.get<DevicePermission[]>('/device-permissions', { params }),

  revokeDevice: (deviceId: string) =>
    adminApiClient.delete(`/devices/${deviceId}`),

  // Connection Nodes
  getConnectionNodes: (params?: { region?: string; status?: string }) =>
    adminApiClient.get<ConnectionNode[]>('/connection-nodes', { params }),

  setNodeMaintenance: (nodeId: string, maintenanceMode: number) =>
    adminApiClient.put(`/connection-nodes/${nodeId}/maintenance?maintenance_mode=${maintenanceMode}`),

  updateNodeCapacity: (nodeId: string, capacity: number) =>
    adminApiClient.put(`/connection-nodes/${nodeId}/capacity?capacity=${capacity}`),

  deleteNode: (nodeId: string) =>
    adminApiClient.delete(`/connection-nodes/${nodeId}`),

  // Friend Management
  getUserFriends: (userId: string) =>
    adminApiClient.get<Friend[]>(`/users/${userId}/friends`),

  // Email Queue Management (Redis-based, current tasks)
  getEmailQueueStats: () =>
    adminApiClient.get<EmailQueueStats>('/email-queue/stats'),

  getPendingEmails: () =>
    adminApiClient.get<{ count: number; tasks: PendingEmailTask[] }>('/email-queue/pending'),

  getFailedEmails: () =>
    adminApiClient.get<{ count: number; failed_tasks: FailedEmailTask[] }>('/email-queue/failed'),

  retryEmailTask: (taskId: string) =>
    adminApiClient.post(`/email-queue/retry/${taskId}`),

  // Email Logs (Database-backed, persistent history)
  getEmailLogHistory: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    email_type?: string;
    status?: string;
    user_id?: string;
    days?: number;
  }) =>
    adminApiClient.get<EmailLogListResponse>('/email-logs/history', { params }),

  getEmailLogStats: (days?: number) =>
    adminApiClient.get<EmailLogStats>('/email-logs/stats', { params: { days } }),

  getEmailLog: (emailLogId: string) =>
    adminApiClient.get<EmailLog>(`/email-logs/${emailLogId}`),

  // User Analytics (Issue #127)
  getUserAnalyticsDashboard: () =>
    adminApiClient.get<UserAnalyticsDashboard>('/users/analytics/dashboard'),

  getUserActivityDetail: (userId: string) =>
    adminApiClient.get<UserActivityDetail>(`/users/${userId}/activity`),

  bulkUpdateUsers: (data: {
    user_ids: string[];
    action: 'activate' | 'deactivate' | 'delete';
    reason?: string;
  }) =>
    adminApiClient.post('/users/bulk/update', data),

  exportUsers: (format: 'csv' | 'json', activeOnly: boolean = false) =>
    adminApiClient.get(`/users/export`, {
      params: { format, active_only: activeOnly },
      responseType: 'blob',
    }),

  // Email Analytics (Issue #126)
  getEmailAnalyticsDashboard: (days?: number) =>
    adminApiClient.get<EmailAnalyticsDashboard>('/email-analytics/dashboard', {
      params: { days },
    }),

  getEmailTrends: (period: 'day' | 'week' | 'month' = 'day') =>
    adminApiClient.get<EmailTrends>('/email-analytics/trends', {
      params: { period },
    }),

  getEmailWorkerStatus: () =>
    adminApiClient.get<EmailWorkerStatus>('/email-analytics/worker-status'),

  bulkRetryFailedEmails: (limit: number = 50) =>
    adminApiClient.post('/email-queue/bulk-retry', null, {
      params: { limit },
    }),
};
